import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'

import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import ExperienceDetailView from './ExperienceListingDrawer/ExperienceDetailView'
import type { DrawerExperience } from './ExperienceListingDrawer/ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './ExperienceListingDrawer/mapExperienceDetailToDrawer'
import { formatExperienceHeaderSubtitle } from './ExperienceListingDrawer/formatExperienceHeaderSubtitle'
import useExperienceDetail from '../hooks/useExperienceDetail'
import useOfferReviews from '../hooks/useOfferReviews'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from './TripDrawerAssistantBridge'
import DrawerDetailLoadingSkeleton from './DrawerDetailLoadingSkeleton'
import { formatTripDrawerMapLabel } from './formatTripDrawerMapLabel'

const ErrorWrapper = styled(Group)`
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

interface Props {
  offerId: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  onShowMap?: (experience: DrawerExperience) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  scrollToPricing?: boolean
  checkoutSource?: App.CheckoutSource
}

function ChatExperienceDetailDrawer({
  offerId,
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  animation,
  onShowMap,
  itineraryData,
  onSendMessage,
  scrollToPricing,
  checkoutSource,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const { experienceDetail, isLoading, isError, refetch } = useExperienceDetail(
    {
      offerId: isOpen ? offerId : undefined,
      currencyCode: currency,
      enabled: isOpen,
    },
  )
  const standaloneOffer = useExperience({
    experienceId: isOpen ? offerId : undefined,
    currentCurrency: currency,
    currentRegionCode: geo.currentRegionCode,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    enabled: isOpen && !!experienceDetail,
  })

  const drawerExperience = useMemo(() => {
    if (!experienceDetail) return undefined
    return mapExperienceDetailToDrawer(experienceDetail)
  }, [experienceDetail])

  const handleShowMap = useCallback(() => {
    if (drawerExperience) {
      onClose()
      onShowMap?.(drawerExperience)
    }
  }, [onClose, onShowMap, drawerExperience])

  const hasGeoLocation = !!drawerExperience?.geoLocation
  const showTitleInContent =
    shouldRenderTripDrawerTitleInContent(drawerPresentation)

  const drawerHeader = useMemo(() => {
    if (!drawerExperience || showTitleInContent) return undefined

    return (
      <TripDrawerHeaderTitle
        title={drawerExperience.name}
        subtitle={formatExperienceHeaderSubtitle(drawerExperience)}
      />
    )
  }, [drawerExperience, showTitleInContent])

  const headerActions = useMemo(() => {
    if (!drawerExperience) return undefined
    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeoLocation && onShowMap && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMap}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {itineraryData && onSendMessage && (
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={drawerExperience.name}
            offerId={drawerExperience.offerId}
            offerType="experience"
            coordinates={drawerExperience.geoLocation}
            variant="outline"
            onAfterAction={onClose}
          />
        )}
      </Group>
    )
  }, [
    drawerExperience,
    drawerPresentation,
    hasGeoLocation,
    handleShowMap,
    intl,
    itineraryData,
    onClose,
    onSendMessage,
    onShowMap,
  ])

  const failedToLoadLabel = intl.formatMessage({
    id: 'clAQJsTYp1RaYNVY1-Rz',
    defaultMessage: 'Failed to load experience details.',
    description:
      'Error message shown when experience offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description:
      'Button label to retry loading hotel offer details after an error',
  })

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      headerActions={headerActions}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={drawerExperience?.name}
          isOpen={isOpen}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen && !!drawerExperience}
        contextLabel={drawerExperience?.name}
      />
      {isLoading && (
        <DrawerDetailLoadingSkeleton />
      )}
      {isError && !isLoading && !drawerExperience && (
        <ErrorWrapper
          direction="vertical"
          gap={8}
          horizontalAlign="center"
          verticalAlign="center"
        >
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={() => refetch()}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {drawerExperience && (
        <ExperienceDetailView
          experience={drawerExperience}
          standaloneOffer={standaloneOffer.data}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          onAfterAddToTrip={onClose}
          scrollToPricing={scrollToPricing}
          checkoutSource={checkoutSource}
          showTitleInContent={showTitleInContent}
          showLocationInContent={showTitleInContent}
          onEnlargeMap={hasGeoLocation && onShowMap ? handleShowMap : undefined}
        />
      )}
    </TripDrawer>
  )
}

export default ChatExperienceDetailDrawer
